import { ResponsiveBullet } from '@nivo/bullet';
import { curr } from 'helpers/curr';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { HeroIconBuilding } from 'toolbox';
import { ReactComponent as IconInformation } from '../../../assets/icons/icon-info.svg';
import BankAccountsModal from '../../BankAccountsModal/BankAccountsModal';
import Button from '../../Button';
import DashboardBankAccountsTable from '../../DashboardBankAccountsTable/DashboardBankAccountsTable';
import { DashboardCurrency } from '../dashboardFilters.machine';

interface Props {
  aggr_balance: number;
  banks: {
    name: string;
    accounts: number;
    aggr_balance_gbp: number;
    aggr_balance_eur: number;
    aggr_balance_usd: number;
    aggr_yield_gbp?: number;
    logo: string;
    limit_pct: number;
    pct_of_total_balance: number;
  }[];
  currency: DashboardCurrency;
  // TODO
  bank_accounts: any[];
}

// the blue bar area
const CustomRange = ({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
}) => (
  <g>
    <defs>
      <clipPath id="round-corner">
        <rect x="0" y="0" width={width} height={height} rx="5" ry="5" />
      </clipPath>
    </defs>
    <rect
      x={x - 2}
      y={y}
      // clip-path={`url(#round-corner)`}
      width={Math.max(0, width - 2)}
      height={height}
      fill={color}
      onMouseEnter={null}
      onMouseMove={null}
      onMouseLeave={null}
    />
  </g>
);

// The red limit bar
const CustomMeasure = ({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  headroom = 0,
}) => (
  <g textAnchor="right">
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop
          offset="0%"
          style={{
            stopColor: headroom > 0 ? '#EBF8FF' : '#FC8181',
            stopOpacity: 0,
          }}
        />
        <stop offset="80%" style={{ stopColor: '#C53030', stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <rect
      x={x}
      y={height}
      width={Math.max(0, width - 2)}
      height={20}
      opacity={0.9}
      fill="url(#grad1)"
      onMouseEnter={null}
      onMouseMove={null}
      onMouseLeave={null}
    />
  </g>
);

// the red line at the end of the limit bar
const CustomMarker = ({ x, size }) => (
  <g
    transform={`translate(${x},0)`}
    onMouseEnter={null}
    onMouseMove={null}
    onMouseLeave={null}
  >
    <line
      x1={-2}
      x2={-2}
      y1={0}
      y2={size + 20}
      stroke="#C53030"
      strokeWidth={2}
      fill="none"
    />
  </g>
);

const roundToNearest10 = (x) => Math.round(x / 10) * 10;

const ChartBankExposure = (props: Props) => {
  return (
    <div className="w-full h-full bg-white border border-gray-400 rounded-lg shadow-sm">
      <h2 className="flex items-start justify-between m-5 font-semibold text-gray-800">
        <span className="mr-5">Bank Exposure</span>
      </h2>
      <Banks {...props} />
    </div>
  );
};

const Banks = ({ banks, aggr_balance, currency, bank_accounts }: Props) => {
  let key: 'aggr_balance_gbp' | 'aggr_balance_usd' | 'aggr_balance_eur' =
    'aggr_balance_gbp';

  if (currency === 'EUR') {
    key = 'aggr_balance_eur';
  } else if (currency === 'USD') {
    key = 'aggr_balance_usd';
  }
  const targetRef = useRef(null);
  const [width, setWidth] = useState(0);
  const resizeHandler = () => setWidth(targetRef.current?.offsetWidth);
  useLayoutEffect(() => {
    if (targetRef?.current) {
      setTimeout(() => setWidth(targetRef.current?.offsetWidth), 100);
      setTimeout(() => setWidth(targetRef.current?.offsetWidth), 2000);
      window.addEventListener('resize', resizeHandler);
    }
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  let highest_bank_limit_or_bal = 0;
  if (banks?.length)
    banks.forEach((bank) => {
      if (bank?.limit_pct > highest_bank_limit_or_bal)
        highest_bank_limit_or_bal = Math.min(
          100,
          roundToNearest10(bank?.limit_pct),
        );
      if (bank?.pct_of_total_balance > highest_bank_limit_or_bal)
        highest_bank_limit_or_bal = Math.min(
          100,
          roundToNearest10(bank?.pct_of_total_balance),
        );
    });

  return (
    <div className="w-full border-t border-gray-200" ref={targetRef}>
      {!isNaN(width) &&
        banks?.length > 0 &&
        banks.map((bank, i) => {
          const limit_gbp = Math.max(0, aggr_balance) * (bank?.limit_pct / 100);
          const headroom = limit_gbp - bank?.[key];

          return (
            <div
              key={i}
              className="flex items-stretch w-full border-b border-gray-200"
            >
              <BankAccountsModal
                title={`Accounts with ${bank?.name}`}
                button={(show) => (
                  <div
                    className="flex items-stretch cursor-pointer hover:underline"
                    key={i}
                    onClick={show}
                  >
                    <div
                      className="flex items-center"
                      style={{
                        width: '60px',
                        height: '60px',
                        margin: '0 15px',
                      }}
                    >
                      {bank?.logo ? (
                        <img src={bank?.logo} alt={bank?.name} />
                      ) : (
                        <HeroIconBuilding className="w-full h-full p-2 text-gray-400 fill-current" />
                      )}
                    </div>
                    <div
                      className="flex flex-col justify-center"
                      style={{ width: '200px', margin: '10px' }}
                    >
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {bank.name}
                      </p>
                      <p className="text-xs font-semibold text-blue-700">
                        {curr(bank?.[key], currency)}
                      </p>
                      <p
                        className={`text-xs truncate ${
                          headroom <= 0
                            ? 'text-red-700 font-semibold'
                            : 'text-green-700 opacity-75'
                        }`}
                      >
                        {headroom >= 0
                          ? `${curr(headroom, currency, true)} under limit`
                          : `${curr(headroom, currency, true).replace(
                              '-',
                              '',
                            )} over limit`}
                      </p>
                    </div>
                  </div>
                )}
                content={() => (
                  <DashboardBankAccountsTable
                    bank_accounts={bank_accounts}
                    bankName={bank?.name}
                  />
                )}
                footer={(hide) => <Button onClick={hide}>Close</Button>}
                style={{ maxWidth: '900px!important' }}
              />

              <div
                className="flex flex-col justify-center flex-grow flex-shrink-0"
                style={{ maxWidth: width - 320 }}
              >
                <AxisPercentageIndicator
                  label="BAL"
                  value={bank?.pct_of_total_balance}
                  valueLabel={`${bank?.pct_of_total_balance
                    .toFixed(2)
                    .replace(/(\.[1-9]+)([0]+)$/, '$1')}%`}
                  maxValue={highest_bank_limit_or_bal}
                  maxValueLabel={`${highest_bank_limit_or_bal}%`}
                  width={width - 320}
                  textClass="text-blue-600 mt-1 -mb-px"
                />
                <div
                  className="overflow-hidden border-r-2 rounded-l"
                  style={{ width: width - 320, height: 20 }}
                >
                  <ResponsiveBullet
                    data={[
                      {
                        id: bank?.name,
                        ranges: [
                          Math.max(0.01, bank?.pct_of_total_balance),
                          headroom > 0 ? bank?.limit_pct : 0,
                          highest_bank_limit_or_bal,
                        ],
                        measures: [bank?.limit_pct],
                        markers: [bank?.limit_pct],
                      },
                    ]}
                    titleAlign="end"
                    titleOffsetX={-9999}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    rangeComponent={CustomRange}
                    markerComponent={CustomMarker}
                    measureComponent={(props) => (
                      <CustomMeasure headroom={headroom} {...props} />
                    )}
                    rangeColors={[
                      headroom >= 0 ? '#3182CE' : '#FC8181',
                      headroom >= 0 ? '#F0FFF4' : '#FFFFFF',
                      '#FFFFFF',
                    ]}
                    animate={false}
                  />
                </div>
                <AxisPercentageIndicator
                  label="LIM"
                  value={bank?.limit_pct}
                  valueLabel={`${bank?.limit_pct}%`}
                  maxValue={highest_bank_limit_or_bal}
                  maxValueLabel={`${highest_bank_limit_or_bal}%`}
                  width={width - 320}
                  textClass="text-red-700 mb-1"
                  showMaxValue
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

interface AxisPercentageIndicatorProps {
  label: string;
  value: number;
  valueLabel?: React.ReactNode;
  maxValue: number;
  maxValueLabel?: React.ReactNode;
  width?: number;
  textClass: string;
  showMaxValue?: boolean;
}

const AxisPercentageIndicator = ({
  label,
  value,
  valueLabel = value.toString(),
  maxValue,
  maxValueLabel = maxValue.toString(),
  width = 0,
  textClass = '',
  showMaxValue = false,
}: AxisPercentageIndicatorProps) => {
  return (
    <div className="flex w-full overflow-hiddden">
      <div className={`${textClass} text-xs opacity-75`} style={{ width: 22 }}>
        {label}
      </div>
      <div
        className={`${textClass} text-right text-xs`}
        style={{ width: (width / maxValue) * value - 24 }}
      >
        {valueLabel}
      </div>
      {showMaxValue && maxValue < 100 && (
        <div className="flex-grow h-4 pr-1 text-xs text-right text-gray-400 border-r-2">
          {maxValueLabel}
        </div>
      )}
    </div>
  );
};

export default ChartBankExposure;
